import './ChartTooltipV2.scss';
import cn from 'classnames';
import { ReactNode, FC, memo } from 'react';

export interface PayloadItem {
  label: ReactNode;
  value: string | number | undefined;
  color: string | undefined;
  keyShape?: KeyShape;
  styles?: Record<string, any>;
  keyOpacity?: number;
  shape?: string;
  iconClassName?: string;
}

export enum KeyShapeOptions {
  CIRCLE = 'circle',
  SQUARE = 'square',
  LINE = 'line',
  DASHED_LINE = 'dashedLine',
  FILLED_RECT = 'filledRect'
}
export type KeyShape = `${KeyShapeOptions}`;

type StringArrayOfMaxLength2 = readonly [string?, string?];
type StringOrNumberArrayOfMaxLength2 = readonly [(string | number)?, (string | number)?];

interface ChartTooltipV2Props {
  headers: StringArrayOfMaxLength2;
  // The small labels above each header when there's a comparison period.
  headerSuperLabels?: StringArrayOfMaxLength2;
  subHeaderLabel?: string | ReactNode;
  subHeaderValue?: string | ReactNode;
  footerLabels?: StringArrayOfMaxLength2;
  footerValues?: StringOrNumberArrayOfMaxLength2;
  items: PayloadItem[];
  itemKeyShape?: KeyShape;
  comparisonItems?: PayloadItem[];
  comparisonItemKeyShape?: KeyShape;
  reverseItems?: boolean;
  decorateLabelsWithColon?: boolean;
}
export type TooltipPassthroughProps = Partial<Omit<ChartTooltipV2Props, 'headers, items'>>;

export const ChartTooltipV2: FC<ChartTooltipV2Props> = ({
  headers,
  headerSuperLabels = ['Primary Period', 'Comparison Period'],
  subHeaderLabel = '',
  subHeaderValue = '',
  footerLabels = ['Total:', 'Total:'],
  footerValues = [],
  items,
  comparisonItems,
  itemKeyShape = 'line',
  comparisonItemKeyShape = 'dashedLine',
  reverseItems = false,
  decorateLabelsWithColon = true
}) => {
  const [primaryHeader, comparisonHeader] = headers;
  const [primarySuperLabel, comparisonSuperLabel] = headerSuperLabels;
  const [primaryFooterLabel, comparisonFooterLabel] = footerLabels;
  const [primaryFooterValue, comparisonFooterValue] = footerValues;
  const hasComparisonPeriod = Boolean(comparisonHeader && comparisonItems);
  const displayedItems = reverseItems ? [...items].reverse() : items;

  return (
    <div className='chart-tooltip-v2'>
      <div
        className={`chart-tooltip-v2-container ${hasComparisonPeriod ? 'has-comparison-period' : ''}`}
      >
        <div className='tooltip-header'>
          {hasComparisonPeriod && <div className='header-super-label'>{primarySuperLabel}</div>}
          {primaryHeader}
        </div>
        {(subHeaderLabel || subHeaderValue) && (
          <div className='tooltip-subheader'>
            <div className='subheader-label'>{subHeaderLabel}</div>
            <div className='subheader-value'>{subHeaderValue}</div>
          </div>
        )}
        <div className='item-list'>
          {displayedItems.map((item, i) => {
            const { label, value, color, keyShape, styles, keyOpacity } = item;
            if (!value) {
              return null;
            }
            return (
              <div
                className={`item-row ${hasComparisonPeriod ? 'has-comparison-period' : ''}`}
                key={i}
              >
                <div className='label-container'>
                  <ColorKey shape={keyShape ?? itemKeyShape} color={color} opacity={keyOpacity} />
                  <div className='label' style={{ ...styles }}>
                    {label}
                    {decorateLabelsWithColon ? ': ' : ''}
                  </div>
                </div>
                <div className={`value ${hasComparisonPeriod ? 'has-comparison-period' : ''}`}>
                  {value}
                </div>
              </div>
            );
          })}
        </div>
        {primaryFooterValue && (
          <div className='footer'>
            <div className='footer-label'>{primaryFooterLabel}</div>
            <div className='footer-value'>{primaryFooterValue}</div>
          </div>
        )}
      </div>

      {hasComparisonPeriod && (
        <div className='chart-tooltip-v2-container'>
          <div className='tooltip-header'>
            <div className='header-super-label'>{comparisonSuperLabel}</div>
            {comparisonHeader}
          </div>
          <div className='item-list'>
            {(comparisonItems || []).map((item, i) => {
              const { label, value, color, styles } = item;
              if (!value) {
                return null;
              }
              return (
                <div
                  className={`item-row ${hasComparisonPeriod ? 'has-comparison-period' : ''}`}
                  key={i}
                >
                  <div className='label-container'>
                    <ColorKey shape={comparisonItemKeyShape} color={color} />
                    <div className='label' style={{ ...styles }}>
                      {label}
                      {': '}
                    </div>
                  </div>
                  <div className='value has-comparison-period'>{value}</div>
                </div>
              );
            })}
          </div>
          {comparisonFooterValue && (
            <div className='footer'>
              <div className='footer-label'>{comparisonFooterLabel}</div>
              <div className='footer-value'>{comparisonFooterValue}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface ColorKeyProps {
  shape: KeyShape;
  color: string | undefined;
  opacity?: number;
}

export const ColorKey: FC<ColorKeyProps> = memo(({ shape, color, opacity = 1 }) => {
  switch (shape) {
    case 'dashedLine':
      return (
        <svg
          className='color-key dashed-line'
          viewBox='0 0 20 12'
          width={16}
          height={12}
          style={{ opacity }}
        >
          <path d='M 0 5 H 20' stroke={color} strokeWidth='4' strokeDasharray='4' />
        </svg>
      );
    // When using a filledRect, the parent component is responsible for
    // rendering a patternDefinition for the provided fill
    case 'filledRect':
      return (
        <svg className='color-key filled-rect' width={16} height={12} style={{ opacity }}>
          <rect className='square' fill={color} />
        </svg>
      );
    default:
      return (
        <div
          className={cn('color-key', { [shape]: shape })}
          style={{ backgroundColor: color, opacity }}
        />
      );
  }
});
